import { useGetAccommodationTypes } from "@/actions/hooks/useGetAccommodationTypes.tsx";
import Container from "@/components/Container/Container.tsx";
import { RangePicker } from "@/components/Inputs/RangePicker/RangePicker.tsx";
import { SelectOption } from "@/components/Inputs/Select/SelectProps.ts";
import { clearReservationOfferURLParam } from "@/helpers/clearReservationOfferURLParam.ts";
import { filterDates } from "@/helpers/filterDates.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { useGetAccommodationTypeTranslated } from "@/helpers/translation/useGetAccommodationTypesTranslated.ts";
import { useHideOnScroll } from "@/helpers/useHideOnScroll.ts";
import { PickingHeaderProps } from "@/pages/AccommodationPicking/components/PickingHeader/PickingHeaderProps.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/components/Inputs/Select/Select.tsx";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";
import { Bed, CalendarDays, Search } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

export const PickingHeader = (props: PickingHeaderProps) => {
  const { t } = useTranslation();
  const { shadowRoot } = useShadowRoot();
  const isDisabled = useCallback(
    () => !!shadowRoot.querySelector(".c-be-range-picker-wrapper--open"),
    [shadowRoot],
  );
  const { isHidden, isScrolled } = useHideOnScroll({ isDisabled, maxWidth: Infinity });
  const { accommodationTypes, getAccommodationTypesLoading } = useGetAccommodationTypes();
  const { getTranslatedAccommodationTypesData } = useGetAccommodationTypeTranslated();
  const {
    defaultDateRange,
    setDefaultDateRange,
    availableRateGroups,
    excludeDates,
    setAccommodationTypeId,
    accommodationTypeId,
    rootAttributes,
    resetCartItems,
  } = useEngineStore(
    useShallow((state) => ({
      defaultDateRange: state.defaultDateRange,
      setDefaultDateRange: state.setDefaultDateRange,
      availableRateGroups: state.availableRateGroups,
      excludeDates: state.rootAttributes[RootAttribute.ExcludeDates],
      setAccommodationTypeId: state.setAccommodationTypeId,
      accommodationTypeId: state.accommodationTypeId,
      rootAttributes: state.rootAttributes,
      resetCartItems: state.resetCartItems,
    })),
  );

  const hasExcludedType = useMemo(
    () => availableRateGroups.some((ARG) => !ARG.isParcel),
    [availableRateGroups],
  );

  const accommodationTypeOptions: SelectOption[] = useMemo(() => {
    return accommodationTypes?.map((accType) => {
      const translatedData = getTranslatedAccommodationTypesData(accType);
      return {
        id: accType.id,
        name: translatedData?.name || accType.name,
        code: accType.code,
      };
    });
  }, [accommodationTypes, getTranslatedAccommodationTypesData]);

  // Hide the category picker when the org disabled it, or when there is at most
  // one accommodation type to choose from (nothing to pick). SW-8680.
  const hideCategoryPicker =
    !!rootAttributes[RootAttribute.HideCategoryPicker] || accommodationTypeOptions.length <= 1;

  const handleSelectAccommodationType = (code: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(URLParam.AccommodationType, code);
    window.history.replaceState(window.history.state, "", `?${urlParams.toString()}`);

    setAccommodationTypeId(code);
  };

  useEffect(() => {
    if (defaultDateRange[0] && defaultDateRange[1]) {
      props.handleFetchAvailableRGs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDateRange, accommodationTypeId]);

  return (
    <section className={`c-picking-header${isHidden ? " is-hidden" : ""}${isScrolled ? " is-scrolled" : ""}`}>
      <Container className={"headerContainer"}>
        <div
          className={`${
            hideCategoryPicker
              ? "c-be-accommodation-picker-wrapper__no-category"
              : "c-be-accommodation-picker-wrapper"
          }`}
        >
          <div className="c-be-accommodation-picker-wrapper__left" data-label-dates={t("checkInCheckOut")}>
            <RangePicker
              onChange={(dates) => {
                setDefaultDateRange(dates);
                resetCartItems();

                // clear reservationOffer from url & store as it's no longer valid
                // because dateRange change clears the selected state
                clearReservationOfferURLParam();
              }}
              startDate={defaultDateRange[0]}
              endDate={defaultDateRange[1]}
              filterDate={
                (getAPIUrl().includes("simuni") && hasExcludedType && excludeDates) ||
                getAPIUrl().includes("olea")
                  ? filterDates
                  : undefined
              }
              prefixIcon={<CalendarDays className="c-picker-mobile-icon" size={18} />}
            />
            {/*<div className="c-guest-count-input-wrap">*/}
            {/*  <GuestCountInput*/}
            {/*    // Note: Adults only are used as guests in the initial screen for searching*/}
            {/*    value={defaultAdults}*/}
            {/*    onChange={setDefaultAdults}*/}
            {/*  />*/}
            {/*</div>*/}
          </div>
          {hideCategoryPicker ? null : (
            <div className="c-be-accommodation-picker-wrapper__center">
              <Select
                options={accommodationTypeOptions}
                onSelect={handleSelectAccommodationType}
                loading={getAccommodationTypesLoading}
                value={accommodationTypeId}
                optionValueKey="code"
                prefixIcon={<Bed className="c-picker-mobile-icon" size={18} />}
              />
            </div>
          )}
          <div className="c-be-accommodation-picker-wrapper__right">
            <button
              disabled={!defaultDateRange[1]}
              className="c-be-button"
              onClick={props.handleFetchAvailableRGs}
            >
              <span className="c-be-button__label">
                <Search size={20} strokeWidth={3} className="c-be-button__label-icon" />
                <span className="c-be-button__label-text">{t("search")}</span>
              </span>
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
};
